import { supabase, getCurrentUser, executeWithRetry } from '../config/supabase';
import NetworkService from './NetworkService';

/**
 * Unified API Service for SceneFeed
 * Handles all Supabase interactions with proper error handling and data transformation
 */
class ApiService {
  // ===== AUTHENTICATION =====
  
  async signUp(email, password, userData) {
    try {
      console.log('Signing up with:', { email, userData }); // Debug log
      
      // Ensure userData has all required fields for the database trigger
      const metadata = {
        username: userData.username,
        city: userData.city,
        state: userData.state,
        user_type: userData.user_type,
        // Add any additional fields that might be needed
        ...userData
      };
      
      console.log('Structured metadata:', metadata);
      
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: metadata
        }
      });
      
      if (error) {
        console.error('Supabase signup error:', error);
        throw error;
      }
      
      console.log('Signup successful:', data);
      return data;
    } catch (error) {
      console.error('ApiService signUp error:', error);
      throw error;
    }
  }

  async signIn(email, password) {
    try {
      console.log('Signing in with:', { email }); // Debug log
      
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password
      });
      
      if (error) {
        console.error('Supabase signin error:', error);
        throw error;
      }
      
      console.log('Signin successful:', data);
      return data;
    } catch (error) {
      console.error('ApiService signIn error:', error);
      throw error;
    }
  }

  async signOut() {
    const { error } = await supabase.auth.signOut();
    if (error) throw error;
  }

  async getCurrentUser() {
    return await getCurrentUser();
  }

  // ===== USER MANAGEMENT =====

  async createUserProfile(userId, profileData) {
    const { data, error } = await executeWithRetry(async () => {
      return await supabase
        .from('users')
        .insert([{
          id: userId,
          ...profileData
        }])
        .select()
        .single();
    });
    
    if (error) throw error;
    return data;
  }

  async getUserProfile(userId) {
    const { data, error } = await executeWithRetry(async () => {
      return await supabase
        .from('users')
        .select('*')
        .eq('id', userId)
        .single();
    });
    
    if (error) throw error;
    return data;
  }

  async updateUserProfile(userId, updates) {
    const { data, error } = await executeWithRetry(async () => {
      return await supabase
        .from('users')
        .update(updates)
        .eq('id', userId)
        .select()
        .single();
    });
    
    if (error) throw error;
    return data;
  }

  async updateScenePoints(pointsToAdd) {
    const user = await this.getCurrentUser();
    if (!user) throw new Error('User not authenticated');

    try {
      const { data, error } = await executeWithRetry(async () => {
        return await supabase
          .from('users')
          .update({ 
            scene_points: supabase.raw(`scene_points + ${pointsToAdd}`)
          })
          .eq('id', user.id)
          .select('scene_points')
          .single();
      });

      if (error) throw error;

      return { 
        success: true, 
        newTotal: data.scene_points,
        pointsAdded: pointsToAdd
      };
    } catch (error) {
      console.error('Error updating scene points:', error);
      
      // Queue for retry if it's a network error
      if (NetworkService.isNetworkError(error)) {
        NetworkService.addToRetryQueue(() => this.updateScenePoints(pointsToAdd));
      }
      
      return { success: false, error: error.message };
    }
  }

  // ===== SHOW MANAGEMENT =====

  async createShow(showData) {
    try {
      const user = await this.getCurrentUser();
      if (!user) {
        return { success: false, error: 'User not authenticated' };
      }

      const { data, error } = await executeWithRetry(async () => {
        return await supabase
          .from('shows')
          .insert([{
            ...showData,
            poster_id: user.id
          }])
          .select()
          .single();
      });
      
      if (error) {
        console.error('Error creating show:', error);
        return { success: false, error: error.message };
      }
      
      console.log('✅ Show created with ID:', data.id);
      return { success: true, id: data.id, data };
    } catch (error) {
      console.error('Error creating show:', error);
      return { success: false, error: error.message };
    }
  }

  async getShows(filters = {}) {
    const user = await this.getCurrentUser();
    
    let query = supabase
      .from('shows')
      .select(`
        *,
        poster:users!shows_poster_id_fkey (
          id,
          username,
          user_level,
          scene_points
        )
      `)
      .eq('approval_status', 'approved')
      .eq('is_hidden', false)
      .gte('show_date', new Date().toISOString().split('T')[0]) // Only future shows
      .order('show_date', { ascending: true })
      .order('show_time', { ascending: true, nullsLast: true });

    // Apply filters
    if (filters.city) {
      query = query.eq('city', filters.city);
    }
    if (filters.state) {
      query = query.eq('state', filters.state);
    }
    if (filters.genre) {
      query = query.eq('genre', filters.genre);
    }
    if (filters.dateFrom) {
      query = query.gte('show_date', filters.dateFrom);
    }
    if (filters.dateTo) {
      query = query.lte('show_date', filters.dateTo);
    }

    const { data, error } = await executeWithRetry(async () => {
      return await query;
    });
    
    if (error) throw error;
    
    // If user is authenticated, check like status for each show
    if (user) {
      const showIds = data.map(show => show.id);
      
      // Get likes data
      const { data: likesData, error: likesError } = await supabase
        .from('user_likes')
        .select('show_id')
        .eq('user_id', user.id)
        .in('show_id', showIds);
      
      if (likesError) throw likesError;
      
      // Get thanks data
      const { data: thanksData, error: thanksError } = await supabase
        .from('show_thanks')
        .select('show_id')
        .eq('thanker_id', user.id)
        .in('show_id', showIds);
      
      if (thanksError) throw thanksError;
      
      const likedShowIds = new Set(likesData.map(like => like.show_id));
      const thankedShowIds = new Set(thanksData.map(thank => thank.show_id));
      
      // Transform the data to include like and thank status
      const transformedData = data.map(show => ({
        ...show,
        is_liked: likedShowIds.has(show.id),
        is_thanked: thankedShowIds.has(show.id)
      }));
      
      return transformedData;
    }
    
    // If no user, return data with is_liked and is_thanked as false
    return data.map(show => ({
      ...show,
      is_liked: false,
      is_thanked: false
    }));
  }

  async getShowById(showId) {
    const user = await this.getCurrentUser();
    
    const { data, error } = await supabase
      .from('shows')
      .select(`
        *,
        poster:users!shows_poster_id_fkey (
          id,
          username,
          user_level,
          scene_points
        )
      `)
      .eq('id', showId)
      .single();
    
    if (error) throw error;
    
    // Check if user likes this show (gracefully handle table access)
    if (user) {
      let isLiked = false;
      let isThanked = false;

      try {
        const { data: likeData, error: likeError } = await supabase
          .from('user_likes')
          .select('id')
          .eq('user_id', user.id)
          .eq('show_id', showId)
          .single();
        
        if (!likeError || likeError.code === 'PGRST116') {
          isLiked = !!likeData;
        } else {
          console.warn('User likes table not accessible:', likeError);
        }
      } catch (error) {
        console.warn('User likes functionality not available:', error);
      }

      try {
        const { data: thankData, error: thankError } = await supabase
          .from('show_thanks')
          .select('id')
          .eq('thanker_id', user.id)
          .eq('show_id', showId)
          .single();
        
        if (!thankError || thankError.code === 'PGRST116') {
          isThanked = !!thankData;
        } else {
          console.warn('Show thanks table not accessible:', thankError);
        }
      } catch (error) {
        console.warn('Show thanks functionality not available:', error);
      }
      
      return {
        ...data,
        is_liked: isLiked,
        is_thanked: isThanked
      };
    }
    
    return {
      ...data,
      is_liked: false,
      is_thanked: false
    };
  }

  async updateShow(showId, updates) {
    const user = await this.getCurrentUser();
    if (!user) throw new Error('User not authenticated');

    // First, verify the user owns the show (direct check to avoid 406 errors)
    const { data: existingShow, error: checkError } = await supabase
      .from('shows')
      .select('poster_id')
      .eq('id', showId)
      .single();

    if (checkError) {
      throw new Error('Show not found');
    }
    
    if (existingShow.poster_id !== user.id) {
      throw new Error('You can only edit shows you created');
    }

    console.log('Updating show with data:', { showId, updates, userId: user.id });

    const { data, error } = await supabase
      .from('shows')
      .update(updates)
      .eq('id', showId)
      .select()
      .single();
    
    if (error) {
      console.error('Supabase update error:', error);
      return { success: false, error: error.message || error.details || 'Unknown error' };
    }
    
    return { success: true, data };
  }

  async deleteShow(showId) {
    const user = await this.getCurrentUser();
    if (!user) return { success: false, error: 'User not authenticated' };

    const { error } = await supabase
      .from('shows')
      .delete()
      .eq('id', showId)
      .eq('poster_id', user.id); // Ensure user owns the show
    
    if (error) {
      console.error('Delete show error:', error);
      return { success: false, error: error.message || 'Failed to delete show' };
    }

    return { success: true };
  }

  // ===== USER SHOWS (CREATED & LIKED) =====

  async getUserShows() {
    const user = await this.getCurrentUser();
    if (!user) throw new Error('User not authenticated');

    // Get shows created by the user
    const { data: createdShows, error: createdError } = await supabase
      .from('shows')
      .select(`
        *,
        poster:users!shows_poster_id_fkey (
          id,
          username,
          user_level,
          scene_points
        )
      `)
      .eq('poster_id', user.id)
      .order('created_at', { ascending: false });
    
    if (createdError) throw createdError;

    // Try to get shows liked by the user (gracefully handle if table doesn't exist)
    let likedShows = [];
    try {
      const { data, error } = await supabase
        .from('user_likes')
        .select(`
          show_id,
          created_at,
          shows (
            *,
            poster:users!shows_poster_id_fkey (
              id,
              username,
              user_level,
              scene_points
            )
          )
        `)
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });
      
      if (!error) {
        likedShows = data || [];
      } else {
        console.warn('User likes table not accessible:', error);
      }
    } catch (error) {
      console.warn('User likes functionality not available:', error);
    }

    // Transform the data to include show_type and is_liked properties
    const transformedCreatedShows = createdShows.map(show => ({
      ...show,
      show_type: 'created',
      is_liked: false,
      liked_at: null,
      posterUsername: show.poster?.username || 'Unknown User'
    }));

    const transformedLikedShows = likedShows
      .filter(like => like.shows) // Filter out any null shows
      .map(like => ({
        ...like.shows,
        show_type: 'liked',
        is_liked: true,
        liked_at: like.created_at,
        posterUsername: like.shows.poster?.username || 'Unknown User'
      }));

    // Combine and deduplicate (if a user created a show they also liked, prioritize created)
    const allShows = [...transformedCreatedShows];
    const createdShowIds = new Set(createdShows.map(show => show.id));
    
    transformedLikedShows.forEach(show => {
      if (!createdShowIds.has(show.id)) {
        allShows.push(show);
      }
    });

    return allShows.sort((a, b) => {
      // Sort by created_at for created shows, liked_at for liked shows
      const aDate = a.show_type === 'created' ? a.created_at : a.liked_at;
      const bDate = b.show_type === 'created' ? b.created_at : b.liked_at;
      return new Date(bDate) - new Date(aDate);
    });
  }

  // ===== LIKES & INTERACTIONS =====

  async likeShow(showId) {
    const user = await this.getCurrentUser();
    if (!user) throw new Error('User not authenticated');

    try {
      // First check if the user already likes this show
      const isLiked = await this.isShowLiked(showId);
      if (isLiked) {
        // User already likes this show, return success without inserting
        return { id: 'existing', user_id: user.id, show_id: showId };
      }

      const { data, error } = await supabase
        .from('user_likes')
        .insert([{
          user_id: user.id,
          show_id: showId
        }])
        .select()
        .single();
      
      if (error) {
        // If it's a duplicate key error, that's fine - the user already likes it
        if (error.code === '23505') {
          return { id: 'existing', user_id: user.id, show_id: showId };
        }
        throw error;
      }
      
      return data;
    } catch (error) {
      console.warn('Like functionality not available:', error);
      // Return a mock success response if likes aren't available
      return { id: 'unavailable', user_id: user.id, show_id: showId };
    }
  }

  async unlikeShow(showId) {
    const user = await this.getCurrentUser();
    if (!user) throw new Error('User not authenticated');

    try {
      const { data, error } = await supabase
        .from('user_likes')
        .delete()
        .eq('user_id', user.id)
        .eq('show_id', showId)
        .select();
      
      if (error) throw error;
      return data;
    } catch (error) {
      console.warn('Unlike functionality not available:', error);
      // Return a mock success response if likes aren't available
      return { id: 'unavailable', user_id: user.id, show_id: showId };
    }
  }

  async isShowLiked(showId) {
    const user = await this.getCurrentUser();
    if (!user) return false;

    try {
      const { data, error } = await supabase
        .from('user_likes')
        .select('id')
        .eq('user_id', user.id)
        .eq('show_id', showId)
        .maybeSingle();
      
      if (error) {
        console.warn('User likes table not accessible:', error);
        return false;
      }
      return !!data;
    } catch (error) {
      console.warn('User likes functionality not available:', error);
      return false;
    }
  }

  // ===== MODERATION =====

  async getPendingShows() {
    // This should probably be restricted to admins/moderators
    const { data, error } = await supabase
      .from('shows')
      .select(`
        *,
        poster:users!shows_poster_id_fkey (
          id,
          username,
          user_level,
          scene_points
        )
      `)
      .eq('approval_status', 'pending')
      .order('created_at', { ascending: true });
    
    if (error) throw error;
    return data;
  }

  async approveShow(showId) {
    const user = await this.getCurrentUser();
    if (!user) throw new Error('User not authenticated');

    try {
      // Check if show exists and get poster info
      const { data: show, error: showError } = await supabase
        .from('shows')
        .select('poster_id, approval_status')
        .eq('id', showId)
        .single();

      if (showError || !show) {
        return { success: false, message: 'Show not found' };
      }

      if (show.approval_status !== 'pending') {
        return { success: false, message: 'Show is not pending approval' };
      }

      // Prevent self-approval
      if (show.poster_id === user.id) {
        return { success: false, message: 'You cannot approve your own shows' };
      }

      // Check if user already approved this show
      const { data: existingApproval } = await supabase
        .from('show_approvals')
        .select('id')
        .eq('show_id', showId)
        .eq('approver_id', user.id)
        .single();

      if (existingApproval) {
        return { success: true, message: "You have already approved this show." };
      }

      // Get user's current level to determine approval power and points earned
      const { data: userData } = await supabase
        .from('users')
        .select('scene_points, user_level')
        .eq('id', user.id)
        .single();

      // Calculate approval power and points earned based on user level
      let approvalPower = 1;
      let pointsEarned = 1;
      
      if (userData?.scene_points >= 1000) { // Scene Legend
        approvalPower = 3;
        pointsEarned = 3;
      } else if (userData?.scene_points >= 500) { // Scene Veteran  
        approvalPower = 2;
        pointsEarned = 2;
      } else if (userData?.scene_points >= 250) { // Scene Insider
        approvalPower = 2;
        pointsEarned = 2;
      } else if (userData?.scene_points >= 100) { // Trusted Poster
        approvalPower = 1;
        pointsEarned = 2;
      } else {
        approvalPower = 1;
        pointsEarned = 1;
      }

      // Insert approval record (this will trigger database functions)
      const { error: approvalError } = await supabase
        .from('show_approvals')
        .insert([{
          show_id: showId,
          approver_id: user.id,
          approval_power: approvalPower
        }]);

      if (approvalError) {
        if (approvalError.code === '23505') { // Duplicate key error
          return { success: true, message: "You have already approved this show." }; 
        }
        throw approvalError;
      }

      // Award points to the approver
      await supabase.rpc('award_scene_points', {
        target_user_id: user.id,
        points: pointsEarned,
        reason: 'Show approval',
        reference_id: showId
      });

      return { 
        success: true, 
        message: "Show approved successfully!",
        pointsAdded: pointsEarned
      };
    } catch (error) {
      console.error('Error approving show:', error);
      return { 
        success: false, 
        message: error.message || 'Failed to approve show'
      };
    }
  }

  async rejectShow(showId) {
    const user = await this.getCurrentUser();
    if (!user) throw new Error('User not authenticated');

    try {
      // Update show to rejected status
      const { error } = await supabase
        .from('shows')
        .update({ approval_status: 'rejected' })
        .eq('id', showId)
        .select()
        .single();

      if (error) throw error;

      // Award points to the user for rejecting (1 point for rejection)
      await supabase.rpc('award_scene_points', {
        target_user_id: user.id,
        points: 1,
        reason: 'Show rejection',
        reference_id: showId
      });
      
      return { 
        success: true, 
        message: "Show rejected successfully!",
        pointsAdded: 1
      };
    } catch (error) {
      console.error('Error rejecting show:', error);
      return { 
        success: false, 
        message: error.message || 'Failed to reject show'
      };
    }
  }

  async getModerationQueue() {
    const user = await this.getCurrentUser();
    if (!user) throw new Error('User not authenticated');

    // All authenticated users can access moderation queue
    const { data, error } = await supabase
      .from('shows')
      .select(`
        *,
        poster:users!shows_poster_id_fkey (
          id,
          username,
          user_level,
          scene_points
        ),
        show_approvals (
          id,
          approver_id,
          approval_power,
          created_at
        ),
        show_reports (
          id,
          reporter_id,
          category,
          message,
          report_weight,
          created_at
        )
      `)
      .eq('approval_status', 'pending')
      .order('created_at', { ascending: false });
    
    if (error) throw error;
    
    // Transform the data to include approval/rejection status
    const transformedData = data.map(show => {
      const approvedBy = show.show_approvals?.map(a => a.approver_id) || [];
      const rejectedBy = []; // We'll need to track this separately if needed
      
      return {
        ...show,
        approvedBy,
        rejectedBy,
        current_reports: show.show_reports || []
      };
    });
    
    return transformedData;
  }

  async getUserShowsForModeration(userId) {
    const { data, error } = await executeWithRetry(async () => {
      return await supabase
        .from('shows')
        .select(`
          *,
          poster:users!shows_poster_id_fkey (
            id,
            username,
            user_level,
            scene_points
          ),
          show_approvals (
            id,
            approver_id,
            approval_power,
            created_at
          )
        `)
        .eq('poster_id', userId)
        .order('created_at', { ascending: false });
    });
    
    if (error) throw error;
    
    // Transform data to include approval progress
    return data.map(show => ({
      ...show,
      approvalsReceived: show.show_approvals?.length || 0,
      approvalsNeeded: show.required_approvals || 3
    }));
  }

  async getRecentlyApprovedShows() {
    const { data, error } = await executeWithRetry(async () => {
      return await supabase
        .from('shows')
        .select(`
          *,
          poster:users!shows_poster_id_fkey (
            id,
            username,
            user_level,
            scene_points
          )
        `)
        .eq('approval_status', 'approved')
        .gte('created_at', new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString()) // Last 7 days
        .order('created_at', { ascending: false })
        .limit(20);
    });
    
    if (error) throw error;
    return data;
  }

  // ===== REPORTING =====

  async reportShow(showId, userId, category, message) {
    try {
      // Validate report category
      const validCategories = ['spam', 'inappropriate_content', 'wrong_information', 'harassment', 'scam', 'other'];
      if (!validCategories.includes(category)) {
        throw new Error('Invalid report category');
      }

      // Check if user already reported this show
      const { data: existingReport, error: checkError } = await supabase
        .from('show_reports')
        .select('id')
        .eq('show_id', showId)
        .eq('reporter_id', userId)
        .maybeSingle();

      if (checkError) {
        throw checkError;
      }

      if (existingReport) {
        return { success: false, error: 'You have already reported this show' };
      }

      // Create report (database trigger will handle the rest)
      const { error } = await supabase
        .from('show_reports')
        .insert([{
          show_id: showId,
          reporter_id: userId,
          category,
          message: message || ''
        }]);

      if (error) {
        throw error;
      }

      return { success: true };

    } catch (error) {
      console.error('Error reporting show:', error);
      return { success: false, error: error.message };
    }
  }

  // ===== UTILITIES =====

  async thankShow(showId, userId) {
    try {
      // Check if user already thanked this show
      const { data: existingThank } = await supabase
        .from('show_thanks')
        .select('id')
        .eq('show_id', showId)
        .eq('thanker_id', userId)
        .single();

      if (existingThank) {
        return { success: false, error: 'You have already thanked this show' };
      }

      // Insert the thank record
      const { error } = await supabase
        .from('show_thanks')
        .insert([{
          show_id: showId,
          thanker_id: userId
        }]);

      if (error) {
        throw error;
      }

      return { success: true };

    } catch (error) {
      console.warn('Thank functionality not available:', error);
      // Return a mock success response if thanks aren't available
      return { success: true, note: 'Thanks functionality not available' };
    }
  }

  async getCities() {
    const { data, error } = await supabase
      .from('shows')
      .select('city, state')
      .eq('approval_status', 'approved')
      .eq('is_hidden', false);
    
    if (error) throw error;
    
    // Get unique city/state combinations
    const uniqueCities = [...new Set(data.map(show => `${show.city}, ${show.state}`))];
    return uniqueCities.sort();
  }
}

// Export singleton instance
const apiService = new ApiService();
export default apiService; 